/**
 * Session context. A SolidJS resource wraps `/v1/me` — the rest
 * of the app pulls the current [`MeView`] off it. Logging out
 * and re-logging-in refreshes via {@link refresh}.
 *
 * The session itself lives server-side in the HTTP cookie; this
 * file is only a thin cache for rendering decisions.
 */

import {
  Accessor,
  JSX,
  createContext,
  createResource,
  useContext,
} from "solid-js";

import { ApiError, api, type MeView } from "./api";
import { signOut } from "./noctantLogin";

export interface SessionState {
  admin: Accessor<MeView | undefined>;
  /** `true` while the initial /me probe is pending. */
  loading: Accessor<boolean>;
  /** `true` when /me returned 401 — caller should redirect to /login. */
  unauthenticated: Accessor<boolean>;
  /** Re-fetch /me after sign-in. */
  refresh: () => void;
  /** End the console and Noctant sessions, then refresh the local cache. */
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionState>();

export function SessionProvider(props: { children: JSX.Element }) {
  const [resource, { refetch }] = createResource<MeView | null>(async () => {
    try {
      return await api.me();
    } catch (e) {
      if (e instanceof ApiError && e.isUnauthorized) {
        return null;
      }
      throw e;
    }
  });

  const admin = () => (resource() ?? undefined) as MeView | undefined;
  const loading = () => resource.loading;
  const unauthenticated = () => resource.state === "ready" && resource() === null;

  const state: SessionState = {
    admin,
    loading,
    unauthenticated,
    refresh: () => refetch(),
    logout: async () => {
      await signOut();
      refetch();
    },
  };

  return <Ctx.Provider value={state}>{props.children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession called outside <SessionProvider>");
  return ctx;
}

/**
 * Role label used across the UI. Centralises the token→human
 * mapping so a role rename only changes one file.
 */
export function roleLabel(role: MeView["role"]): string {
  switch (role) {
    case "noctant_superadmin":
      return "Noctant superadmin";
    case "operator_admin":
      return "Operator admin";
    case "operator_support":
      return "Operator support";
    case "environment_monitor":
      return "Environment monitor";
  }
}

export function roleBadgeClass(role: MeView["role"]): string {
  switch (role) {
    case "noctant_superadmin":
      return "badge-superadmin";
    case "operator_admin":
      return "badge-admin";
    case "operator_support":
    case "environment_monitor":
      return "badge-support";
  }
}

/** Mutating endpoints are hidden from `operator_support` at the UI
 * layer so those buttons never render — the server still 403s as
 * a backstop, but the UX stays clean. */
export function canMutate(role: MeView["role"]): boolean {
  return role === "noctant_superadmin" || role === "operator_admin";
}
